/* ============================================================================
   Slide 6b — "Nostril & Sleep" — SHARED CONTENT (single source of truth)
   Read by both HTLDSlide6bInner (desktop) and HTLDSlide6bMobile. Edit here
   — English and German side by side; the German object must have exactly the
   same shape as the English one.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";

export const accent6b = "#c76a94";

export interface Seg { t: string; b?: boolean; em?: boolean; }

export interface NostrilPairing { side: string; nadi: string; state: string; desc: string; highlight?: boolean; }

/** A "phase pairing" row drawn on the canvas; `right` marks the highlighted (right-nostril) row. */
export interface PairRow { tag: string; text: string; right?: boolean; }

const en = {
  meta: {
    breadcrumbPre: "Swara Yoga",
    breadcrumbAccent: "The nasal cycle",
    headerTitlePre: "Nostril ",
    headerTitleAccent: "& Sleep",
    titlePre: "One Rhythm, ",
    titleAccentWord: "Two Faces",
    titlePost: "",
    subtitle: "The nostril that leads your breath and the stage your brain is in both shift on the same clock",
    rhythmLabel: "The shared rhythm",
    pairLabel: "How they pair",
    calloutLabel: "Why Dreamfulness cares",
  },
  pairings: [
    { side: "Left Nostril",  nadi: "Ida · Chandra Nadi",  state: "Parasympathetic", desc: "Calming, cooling. Dominant during deep NREM — the body's repair phase." },
    { side: "Right Nostril", nadi: "Pingala · Surya Nadi", state: "Sympathetic",     desc: "Activating, heating. Dominant during REM — brain activity nearly matches waking.", highlight: true },
    { side: "Both Nostrils", nadi: "Sushumna",             state: "Transition",      desc: "Briefly balanced at stage changes — the seam between NREM and REM." },
  ] as NostrilPairing[],
  rhythm: [
    { t: "Swara Yoga observed that nostril dominance switches every " },
    { t: "90–120 minutes", b: true },
    { t: ", day and night. Sleep researcher Nathaniel Kleitman later found the same-length pulse driving NREM–REM alternation and named it the " },
    { t: "Basic Rest-Activity Cycle", b: true },
    { t: " — one ultradian rhythm, observed through two very different lenses." },
  ] as Seg[],
  callout: [
    { t: "If the two rhythms are linked, then shifting nostril dominance on purpose — through the alternate-nostril and left-nostril breathing practices ahead — is a lever on the nervous system. " },
    { t: "Calm the right nostril, and you nudge the body toward the parasympathetic state that precedes REM-rich sleep.", b: true },
  ] as Seg[],
  canvas: {
    /** Y-axis labels, top to bottom: awake, REM, light, deep. */
    stageLabels: ["AWAKE", "REM", "LIGHT", "DEEP"],
    title: "SLEEP STAGE  ×  NOSTRIL DOMINANCE",
    cycle: "Cycle",
    stripTitle: "SWARA · NOSTRIL DOMINANCE",
    /** Single-letter strip labels for left / right. */
    stripLeft: "L",
    stripRight: "R",
    nowTitle: "NOSTRIL DOMINANCE RIGHT NOW",
    left: "LEFT",
    right: "RIGHT",
    open: "OPEN",
    closed: "CLOSED",
    pairingTitle: "PHASE PAIRING",
    pairs: [
      { tag: "LEFT · IDA",        text: "Calming, cooling · pairs with deep NREM" },
      { tag: "RIGHT · PINGALA ◀", text: "Activating · pairs with REM, dreaming", right: true },
      { tag: "SWITCH",            text: "Both nostrils flow briefly at the switch" },
    ] as PairRow[],
  },
};

export type Slide6bContent = typeof en;

const de: Slide6bContent = {
  meta: {
    breadcrumbPre: "Swara Yoga",
    breadcrumbAccent: "Der Nasenzyklus",
    headerTitlePre: "Nasenatmung ",
    headerTitleAccent: "& Schlaf",
    titlePre: "Ein Rhythmus, ",
    titleAccentWord: "zwei Gesichter",
    titlePost: "",
    subtitle: "Führendes Nasenloch und Schlafphase folgen derselben inneren Uhr",
    rhythmLabel: "Der gemeinsame Rhythmus",
    pairLabel: "Wie sie zusammenhängen",
    calloutLabel: "Warum das für Dreamfulness zählt",
  },
  pairings: [
    { side: "Linkes Nasenloch",  nadi: "Ida · Chandra Nadi",  state: "Parasympathikus", desc: "Beruhigend, kühlend. Dominant im tiefen NREM-Schlaf – der Erholungsphase des Körpers." },
    { side: "Rechtes Nasenloch", nadi: "Pingala · Surya Nadi", state: "Sympathikus",     desc: "Aktivierend, wärmend. Dominant im REM – Hirnaktivität fast wie im Wachzustand.", highlight: true },
    { side: "Beide Nasenlöcher", nadi: "Sushumna",             state: "Übergang",        desc: "Beim Phasenwechsel kurz im Gleichgewicht – die Nahtstelle zwischen NREM und REM." },
  ],
  rhythm: [
    { t: "Swara Yoga beobachtete, dass die Nasendominanz alle " },
    { t: "90–120 Minuten", b: true },
    { t: " wechselt, Tag und Nacht. Schlafforscher Nathaniel Kleitman fand später denselben Takt im Wechsel von NREM und REM und nannte ihn " },
    { t: "Basic Rest-Activity Cycle", b: true },
    { t: " – ein ultradianer Rhythmus, aus zwei Blickwinkeln gesehen." },
  ],
  callout: [
    { t: "Hängen beide Rhythmen zusammen, wird die Nasendominanz – über die folgende Wechsel- und Linksatmung – zum Hebel für dein Nervensystem. " },
    { t: "Beruhige das rechte Nasenloch, und du lenkst den Körper in den parasympathischen Zustand vor REM-reichem Schlaf.", b: true },
  ],
  canvas: {
    stageLabels: ["WACH", "REM", "LEICHT", "TIEF"],
    title: "SCHLAFPHASE  ×  NASENDOMINANZ",
    cycle: "Zyklus",
    stripTitle: "SWARA · NASENDOMINANZ",
    stripLeft: "L",
    stripRight: "R",
    nowTitle: "GERADE DOMINANTES NASENLOCH",
    left: "LINKS",
    right: "RECHTS",
    open: "OFFEN",
    closed: "GESCHLOSSEN",
    pairingTitle: "ZUORDNUNG",
    pairs: [
      { tag: "LINKS · IDA",        text: "Beruhigend, kühlend · gehört zum tiefen NREM" },
      { tag: "RECHTS · PINGALA ◀", text: "Aktivierend · gehört zum REM-Schlaf, zum Träumen", right: true },
      { tag: "WECHSEL",            text: "Beim Wechsel fließt der Atem kurz durch beide Seiten" },
    ],
  },
};

export const slide6bContent: ByLang<Slide6bContent> = { en, de };
export const useSlide6b = () => useL(slide6bContent);
